// Module 4 / Slice 5b — DimensionTagsTab.
//
// Reads organization.employee_dimension_tag rows scoped to the active
// employee. Open-ended (tag_type, tag_value) taxonomy per Q-MOD4-
// DIMENSION-TAG-SCHEMA.
//
// Write actions deferred to a Phase-4 follow-up. PATTERNS.md §2.2.

import { useQuery } from "@tanstack/react-query";
import { workforceApiClient } from "../../../api/client";
import { useI18n } from "../../../i18n/I18nContext";

interface DimensionTagsTabProps {
  employeeId: string;
}

export function DimensionTagsTab({
  employeeId,
}: DimensionTagsTabProps): JSX.Element {
  const { t } = useI18n();

  const query = useQuery({
    queryKey: ["workforce", "employee", employeeId, "dimension-tags"] as const,
    queryFn: async () => {
      const response = await workforceApiClient.GET(
        "/workforce/employees/{employeeId}/dimension-tags",
        {
          params: {
            path: { employeeId },
            query: { limit: 200 },
          },
        },
      );
      if (!response.data) {
        throw new Error("workforce_dimension_tags_fetch_failed");
      }
      return response.data;
    },
  });

  return (
    <div data-testid="dimension-tags-tab">
      {query.isLoading && <p>{t("login.discovering")}</p>}
      {query.isError && (
        <p role="alert" style={{ color: "var(--rc-color-danger)" }}>
          {(query.error as Error).message}
        </p>
      )}
      {query.data && query.data.items && query.data.items.length === 0 && (
        <p data-testid="dimension-tags-empty">
          {t("workforce.dimensionTags.empty")}
        </p>
      )}
      {query.data && query.data.items && query.data.items.length > 0 && (
        <table className="rc-table">
          <thead>
            <tr>
              <th>{t("workforce.dimensionTags.column.type")}</th>
              <th>{t("workforce.dimensionTags.column.value")}</th>
              <th>{t("workforce.dimensionTags.column.start")}</th>
              <th>{t("workforce.dimensionTags.column.end")}</th>
            </tr>
          </thead>
          <tbody>
            {query.data.items.map((tag) => (
              <tr key={tag.id} data-testid={`dimension-tag-row-${tag.id}`}>
                <td>
                  <code>{tag.tagType}</code>
                </td>
                <td>{tag.tagValue}</td>
                <td>{tag.effectiveStartDate ?? "\u2014"}</td>
                <td>{tag.effectiveEndDate ?? "\u2014"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
